export type Language = 'en' | 'nl'

export const translations = {
  en: {
    // Navigation
    nav: {
      overview: 'Overview',
      users: 'Users',
      userManagement: 'User management',
    },

    // Header
    header: {
      title: 'Gemini Usage Monitor',
      subtitle: 'Real-time analytics and adoption insights',
      lastUpdated: 'Last Updated',
      refresh: 'Refresh',
    },

    // Dashboard Overview
    dashboard: {
      title: 'Overview',
      subtitle: 'Gemini adoption and impact within your organization',
      usageOverTime: 'Usage Over Time',
      usageOverTimeDesc: 'Use the date selector above to adjust the period. Automatically shows per hour for ≤3 days, otherwise per day.',
      usageByAppAction: 'Usage by App and Action',
      allUsers: 'All users',
      searchUser: 'Search user...',
    },

    // Status Summary
    status: {
      title: 'Status Overview',
      adoption: 'Adoption',
      adoptionDesc: 'Regular + Power users',
      powerUsers: 'Power users',
      inactive: 'Inactive',
      stable: 'Stable',
      vsLabel: 'vs',
    },

    // Active Account Adoption
    activeAdoption: {
      title: 'Adoption among active accounts',
      subtitle: '{adopters} of {accounts} active accounts use Gemini',
      note: 'Active = logged in to Workspace in last {days} days',
    },

    // Time Series Chart
    timeChart: {
      perHour: 'Per Hour',
      perDay: 'Per Day',
      hourlyOnlyAvailable: 'Hourly only available up to 30 days',
      totalActivities: 'Total Activities',
      maxActivities: 'Max Activities per',
      hour: 'hour',
      day: 'day',
      numberOfApps: 'Number of Apps',
      noDataAvailable: 'No data available for this period',
      tryDifferentRange: 'Try a different date range',
      activities: 'Activities',
    },

    // User Types Chart
    userTypesChart: {
      title: 'User Types Over Time',
      subtitle: 'Track how user segments evolve throughout the selected period',
      users: 'Users',
    },

    // Usage Chart
    usageChart: {
      perApp: 'Per App',
      perAction: 'Per Action',
      perAppAction: 'Per App & Action',
      usage: 'Usage',
      users: 'Users',
      totalApps: 'Total Apps',
      mostUsed: 'Most Used',
      totalUsage: 'Total Usage',
      times: 'times',
      totalActions: 'Total Actions',
      mostPopular: 'Most Popular',
      totalCombinations: 'Total Combinations',
      topCombination: 'Top Combination',
    },

    // Date Picker
    datePicker: {
      title: 'Date Selection',
      fromDate: 'From Date',
      toDate: 'To Date',
      quickSelect: 'Quick Select',
      today: 'Today',
      last7Days: 'Last 7 Days',
      last30Days: 'Last 30 Days',
      last90Days: 'Last 90 Days',
      selectedPeriod: 'Selected Period',
      currentPeriod: 'Current period:',
      newPeriod: 'New period:',
      compareWith: 'Compare with:',
      days: 'days',
      previousDay: 'Previous Day',
      previousDays: 'Previous {n} Days',
      allDashboardsUseThisPeriod: 'All dashboards use this period',
      apply: 'Apply',
      period: 'Period',
    },

    // Filter Panel
    filters: {
      title: 'Filters',
      apps: 'Apps',
      actions: 'Actions',
      searchApps: 'Search apps...',
      searchActions: 'Search actions...',
      all: 'All',
      none: 'None',
      noAppsFound: 'No apps found',
      noActionsFound: 'No actions found',
      noAppsSelected: 'No apps selected',
      noActionsSelected: 'No actions selected',
      appsScrollMore: '{n} apps - scroll for more',
      actionsScrollMore: '{n} actions - scroll for more',
      resetAll: 'Reset All',
      done: 'Done',
      active: 'active',
    },

    // Users Page
    usersPage: {
      title: 'Users Overview',
      subtitle: 'Detailed analysis of all users and their activity',
      powerUsers: 'Power Users',
      regular: 'Regular',
      lowUsage: 'Low Usage',
      inactive: 'Inactive',
      activities: 'activities',
      filter: 'Filter',
      allUsers: 'All Users',
      inactiveUsers: 'Inactive Users',
      regularUsers: 'Regular Users',
      lowActivityUsers: 'Low Activity Users',
      showAll: 'Show all',
      filters: 'Filters',
      user: 'User',
      segment: 'Segment',
      uniqueActions: 'Unique Actions',
      activeDays: 'Active Days',
      lastActivity: 'Last Activity',
    },

    // User management (admin only)
    users: {
      title: 'User management',
      subtitle: 'Manage who has access to this environment',
      addTitle: 'Add user',
      emailLabel: 'Email address',
      emailPlaceholder: 'name@company.com',
      roleLabel: 'Role',
      roleAdmin: 'Administrator',
      roleUser: 'User',
      add: 'Add',
      adding: 'Adding...',
      colEmail: 'Email',
      colRole: 'Role',
      colStatus: 'Status',
      colAddedBy: 'Added by',
      colAddedOn: 'Added on',
      colActions: 'Actions',
      statusActive: 'Active',
      statusDisabled: 'Disabled',
      you: 'you',
      makeAdmin: 'Make admin',
      makeUser: 'Make user',
      enable: 'Enable',
      disable: 'Disable',
      remove: 'Remove',
      loading: 'Loading users...',
      empty: 'No users yet. Add the first one above.',
      unknown: 'Unknown',
      confirmRemove: 'Remove {email} from this environment?',
      confirmRemoveSelf: 'This is your own account. If you remove {email} you lose access to this environment immediately. Continue?',
      confirmDisableSelf: 'This is your own account. If you disable {email} you lose access to this environment. Continue?',
      confirmDemoteSelf: 'This is your own account. If you change {email} to user you lose access to user management. Continue?',
      added: 'User added',
      addedDetail: '{email} now has access.',
      updated: 'User updated',
      removed: 'User removed',
      removedDetail: '{email} no longer has access.',
      adminOnly: 'Only administrators can manage users.',
      // Error codes from the API
      errWrongDomain: 'This email address does not belong to this environment.',
      errAlreadyExists: 'This user already exists.',
      errLastAdmin: 'The last administrator cannot be removed or demoted.',
      errInvalidEmail: 'Please enter a valid email address.',
      errForbidden: 'You do not have permission to manage users.',
      errGeneric: 'Something went wrong. Please try again.',
      errLoad: 'Could not load users.',
    },

    // Segment badges
    segments: {
      powerUser: 'Power User',
      regular: 'Regular',
      lowUsage: 'Low Usage',
      inactive: 'Inactive',
    },

    // Period Selector (legacy)
    periodSelector: {
      comparePeriod: 'Compare Period',
      choosePeriod: 'Choose which period you want to view',
      week: 'Week',
      last30Days: 'Last 30 Days',
      last90Days: 'Last 90 Days',
      thisWeek: 'THIS WEEK',
      previousWeek: 'Previous week',
      lastThirtyDays: 'LAST 30 DAYS',
      previousThirtyDays: 'Previous 30 days',
      lastNinetyDays: 'LAST 90 DAYS',
      previousNinetyDays: 'Previous 90 days',
    },

    // Errors
    errors: {
      failedToLoad: 'Failed to Load',
      failedToLoadUsers: 'Failed to Load Users',
      failedToLoadChartData: 'Failed to Load Chart Data',
      failedToLoadUsageData: 'Failed to Load Usage Data',
      failedToFilter: 'Failed to Filter Users',
      failedToLoadFilters: 'Failed to Load Filters',
      tryRefreshing: 'Please try refreshing the page.',
      tryAgain: 'Please try again.',
      someOptionsUnavailable: 'Some options may be unavailable.',
    },

    // Common
    common: {
      loading: 'Loading...',
      error: 'Error',
      warning: 'Warning',
      success: 'Success',
    },

    // Auth
    auth: {
      signInWithGoogle: 'Sign in with Google',
      signInPrompt: 'Sign in to view your analytics',
      accessDenied: 'Access denied',
      accessDeniedHint: 'Your account does not have access to this environment.',
      signOut: 'Sign out',
      initialising: 'Setting up your environment, please try again in a moment...',
      unknownTenant: 'This environment does not exist.',
      apexTitle: 'Gemini Usage Monitor',
      apexHint: 'Please go to your organisation’s environment to sign in.',
      popupBlocked: 'Your browser blocked the sign-in popup. Please allow popups and try again.',
      loginFailed: 'Login failed. Please try again.',
      authUnavailable: 'Sign-in is temporarily unavailable. Please try again later.',
    },
  },

  nl: {
    // Navigation
    nav: {
      overview: 'Overzicht',
      users: 'Gebruikers',
      userManagement: 'Gebruikersbeheer',
    },

    // Header
    header: {
      title: 'Gemini Usage Monitor',
      subtitle: 'Real-time analytics en adoptie inzichten',
      lastUpdated: 'Laatst Bijgewerkt',
      refresh: 'Vernieuwen',
    },

    // Dashboard Overview
    dashboard: {
      title: 'Overzicht',
      subtitle: 'Gemini adoptie en impact binnen je organisatie',
      usageOverTime: 'Gebruik Over Tijd',
      usageOverTimeDesc: 'Gebruik de datum selectie hierboven om de periode aan te passen. Automatisch per uur voor ≤3 dagen, anders per dag.',
      usageByAppAction: 'Gebruik per App en Actie',
      allUsers: 'Alle gebruikers',
      searchUser: 'Zoek gebruiker...',
    },

    // Status Summary
    status: {
      title: 'Status Overzicht',
      adoption: 'Adoptie',
      adoptionDesc: 'Regulier + Power users',
      powerUsers: 'Power users',
      inactive: 'Niet-actief',
      stable: 'Stabiel',
      vsLabel: 'vs',
    },

    // Active Account Adoption
    activeAdoption: {
      title: 'Adoptie onder actieve accounts',
      subtitle: '{adopters} van {accounts} actieve accounts gebruiken Gemini',
      note: 'Actief = laatste {days} dagen ingelogd in Workspace',
    },

    // Time Series Chart
    timeChart: {
      perHour: 'Per Uur',
      perDay: 'Per Dag',
      hourlyOnlyAvailable: 'Uurlijks alleen beschikbaar tot 30 dagen',
      totalActivities: 'Totaal Activiteiten',
      maxActivities: 'Max Activiteiten per',
      hour: 'uur',
      day: 'dag',
      numberOfApps: 'Aantal Apps',
      noDataAvailable: 'Geen data beschikbaar voor deze periode',
      tryDifferentRange: 'Probeer een andere datumbereik',
      activities: 'Activiteiten',
    },

    // User Types Chart
    userTypesChart: {
      title: 'Gebruikerstypes Over Tijd',
      subtitle: 'Volg hoe gebruikerssegmenten evolueren gedurende de geselecteerde periode',
      users: 'Gebruikers',
    },

    // Usage Chart
    usageChart: {
      perApp: 'Per App',
      perAction: 'Per Actie',
      perAppAction: 'Per App & Actie',
      usage: 'Gebruik',
      users: 'Gebruikers',
      totalApps: 'Totaal Apps',
      mostUsed: 'Meest Gebruikt',
      totalUsage: 'Totaal Gebruik',
      times: 'keer',
      totalActions: 'Totaal Acties',
      mostPopular: 'Populairste',
      totalCombinations: 'Totaal Combinaties',
      topCombination: 'Top Combinatie',
    },

    // Date Picker
    datePicker: {
      title: 'Datum Selectie',
      fromDate: 'Van Datum',
      toDate: 'Tot Datum',
      quickSelect: 'Snelle Selectie',
      today: 'Vandaag',
      last7Days: 'Laatste 7 Dagen',
      last30Days: 'Laatste 30 Dagen',
      last90Days: 'Laatste 90 Dagen',
      selectedPeriod: 'Geselecteerde Periode',
      currentPeriod: 'Huidige periode:',
      newPeriod: 'Nieuwe periode:',
      compareWith: 'Vergelijken met:',
      days: 'dagen',
      previousDay: 'Vorige Dag',
      previousDays: 'Vorige {n} Dagen',
      allDashboardsUseThisPeriod: 'Alle dashboards gebruiken deze periode',
      apply: 'Toepassen',
      period: 'Periode',
    },

    // Filter Panel
    filters: {
      title: 'Filters',
      apps: 'Apps',
      actions: 'Acties',
      searchApps: 'Zoek apps...',
      searchActions: 'Zoek acties...',
      all: 'Alles',
      none: 'Geen',
      noAppsFound: 'Geen apps gevonden',
      noActionsFound: 'Geen acties gevonden',
      noAppsSelected: 'Geen apps geselecteerd',
      noActionsSelected: 'Geen acties geselecteerd',
      appsScrollMore: '{n} apps - scroll voor meer',
      actionsScrollMore: '{n} acties - scroll voor meer',
      resetAll: 'Reset Alles',
      done: 'Klaar',
      active: 'actief',
    },

    // Users Page
    usersPage: {
      title: 'Gebruikers Overzicht',
      subtitle: 'Gedetailleerde analyse van alle gebruikers en hun activiteit',
      powerUsers: 'Power Users',
      regular: 'Regulier',
      lowUsage: 'Laag Gebruik',
      inactive: 'Niet Actief',
      activities: 'activiteiten',
      filter: 'Filter',
      allUsers: 'Alle Gebruikers',
      inactiveUsers: 'Niet Actieve Gebruikers',
      regularUsers: 'Reguliere Gebruikers',
      lowActivityUsers: 'Lage Activiteit Gebruikers',
      showAll: 'Toon alles',
      filters: 'Filters',
      user: 'Gebruiker',
      segment: 'Segment',
      uniqueActions: 'Unieke Acties',
      activeDays: 'Actieve Dagen',
      lastActivity: 'Laatste Activiteit',
    },

    // User management (admin only)
    users: {
      title: 'Gebruikersbeheer',
      subtitle: 'Beheer wie toegang heeft tot deze omgeving',
      addTitle: 'Gebruiker toevoegen',
      emailLabel: 'E-mailadres',
      emailPlaceholder: 'naam@bedrijf.nl',
      roleLabel: 'Rol',
      roleAdmin: 'Beheerder',
      roleUser: 'Gebruiker',
      add: 'Toevoegen',
      adding: 'Bezig met toevoegen...',
      colEmail: 'E-mail',
      colRole: 'Rol',
      colStatus: 'Status',
      colAddedBy: 'Toegevoegd door',
      colAddedOn: 'Toegevoegd op',
      colActions: 'Acties',
      statusActive: 'Actief',
      statusDisabled: 'Uitgeschakeld',
      you: 'jij',
      makeAdmin: 'Maak beheerder',
      makeUser: 'Maak gebruiker',
      enable: 'Inschakelen',
      disable: 'Uitschakelen',
      remove: 'Verwijderen',
      loading: 'Gebruikers laden...',
      empty: 'Nog geen gebruikers. Voeg hierboven de eerste toe.',
      unknown: 'Onbekend',
      confirmRemove: '{email} verwijderen uit deze omgeving?',
      confirmRemoveSelf: 'Dit is je eigen account. Als je {email} verwijdert, verlies je direct toegang tot deze omgeving. Doorgaan?',
      confirmDisableSelf: 'Dit is je eigen account. Als je {email} uitschakelt, verlies je toegang tot deze omgeving. Doorgaan?',
      confirmDemoteSelf: 'Dit is je eigen account. Als je {email} naar gebruiker wijzigt, verlies je toegang tot gebruikersbeheer. Doorgaan?',
      added: 'Gebruiker toegevoegd',
      addedDetail: '{email} heeft nu toegang.',
      updated: 'Gebruiker bijgewerkt',
      removed: 'Gebruiker verwijderd',
      removedDetail: '{email} heeft geen toegang meer.',
      adminOnly: 'Alleen beheerders kunnen gebruikers beheren.',
      // Foutcodes van de API
      errWrongDomain: 'Dit e-mailadres hoort niet bij deze omgeving.',
      errAlreadyExists: 'Deze gebruiker bestaat al.',
      errLastAdmin: 'De laatste beheerder kan niet worden verwijderd of gedegradeerd.',
      errInvalidEmail: 'Voer een geldig e-mailadres in.',
      errForbidden: 'Je hebt geen rechten om gebruikers te beheren.',
      errGeneric: 'Er ging iets mis. Probeer het opnieuw.',
      errLoad: 'Gebruikers konden niet worden geladen.',
    },

    // Segment badges
    segments: {
      powerUser: 'Power User',
      regular: 'Regulier',
      lowUsage: 'Laag Gebruik',
      inactive: 'Niet Actief',
    },

    // Period Selector (legacy)
    periodSelector: {
      comparePeriod: 'Vergelijk Periode',
      choosePeriod: 'Kies welke periode je wilt bekijken',
      week: 'Week',
      last30Days: 'Laatste 30 Dagen',
      last90Days: 'Laatste 90 Dagen',
      thisWeek: 'DEZE WEEK',
      previousWeek: 'Vorige week',
      lastThirtyDays: 'LAATSTE 30 DAGEN',
      previousThirtyDays: 'Vorige 30 dagen',
      lastNinetyDays: 'LAATSTE 90 DAGEN',
      previousNinetyDays: 'Vorige 90 dagen',
    },

    // Errors
    errors: {
      failedToLoad: 'Laden Mislukt',
      failedToLoadUsers: 'Gebruikers Laden Mislukt',
      failedToLoadChartData: 'Grafiek Data Laden Mislukt',
      failedToLoadUsageData: 'Gebruik Data Laden Mislukt',
      failedToFilter: 'Filteren Mislukt',
      failedToLoadFilters: 'Filters Laden Mislukt',
      tryRefreshing: 'Probeer de pagina te vernieuwen.',
      tryAgain: 'Probeer het opnieuw.',
      someOptionsUnavailable: 'Sommige opties zijn mogelijk niet beschikbaar.',
    },

    // Common
    common: {
      loading: 'Laden...',
      error: 'Fout',
      warning: 'Waarschuwing',
      success: 'Succes',
    },

    // Auth
    auth: {
      signInWithGoogle: 'Inloggen met Google',
      signInPrompt: 'Log in om je analytics te bekijken',
      accessDenied: 'Geen toegang',
      accessDeniedHint: 'Je account heeft geen toegang tot deze omgeving.',
      signOut: 'Uitloggen',
      initialising: 'Je omgeving wordt klaargezet, probeer het zo opnieuw...',
      unknownTenant: 'Deze omgeving bestaat niet.',
      apexTitle: 'Gemini Usage Monitor',
      apexHint: 'Ga naar de omgeving van je organisatie om in te loggen.',
      popupBlocked: 'Je browser blokkeerde het inlogvenster. Sta pop-ups toe en probeer het opnieuw.',
      loginFailed: 'Inloggen mislukt. Probeer het opnieuw.',
      authUnavailable: 'Inloggen is tijdelijk niet beschikbaar. Probeer het later opnieuw.',
    },
  },
} as const

export type TranslationKey = keyof typeof translations.en
